import { useState } from "react";
import { motion } from "framer-motion";
import { Plus, Check, Layers } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import Reveal from "@/components/Reveal";

const ModularGrowth = () => {
  const { t } = useLanguage();
  const [added, setAdded] = useState([]);
  const total = t.modular.base.length + added.length;
  const max = t.modular.base.length + t.modular.addons.length;

  const toggle = (mod) =>
    setAdded((prev) => (prev.includes(mod) ? prev.filter((m) => m !== mod) : [...prev, mod]));

  return (
    <section id="modular" className="relative mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-12 lg:py-32" data-testid="modular-section">
      <div className="grid items-start gap-12 lg:grid-cols-2 lg:gap-16">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.28em] text-cy">{t.modular.label}</p>
          <h2 className="mt-4 font-display text-2xl font-bold leading-[1.15] tracking-tight text-slate-50 sm:text-3xl lg:text-4xl">
            {t.modular.title}
          </h2>
          <p className="mt-5 text-base leading-relaxed text-slate-400">{t.modular.sub}</p>

          <div className="mt-10">
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-em">{t.modular.baseTitle}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {t.modular.base.map((m) => (
                <span key={m} className="flex items-center gap-2 rounded-full border border-em/40 bg-em/10 px-4 py-2 text-sm font-medium text-em">
                  <Check className="h-3.5 w-3.5" />
                  {m}
                </span>
              ))}
            </div>
          </div>

          <div className="mt-8" data-testid="modular-growth-slider">
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">{t.modular.addTitle}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {t.modular.addons.map((m) => {
                const on = added.includes(m);
                return (
                  <button
                    key={m}
                    data-testid={`modular-addon-${m.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => toggle(m)}
                    className={`flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition-all duration-300 ${
                      on
                        ? "border-cy/60 bg-cy/15 text-cy"
                        : "border-cy/15 bg-qcard text-slate-400 hover:border-cy/40 hover:text-slate-200"
                    }`}
                  >
                    {on ? <Check className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
                    {m}
                  </button>
                );
              })}
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="rounded-xl border border-cy/20 bg-panel p-8 sm:p-10">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Layers className="h-5 w-5 text-cy" />
                <span className="font-mono text-xs uppercase tracking-[0.2em] text-slate-400">QC // SYSTEM MAP</span>
              </div>
              <div className="text-right">
                <motion.p
                  key={total}
                  initial={{ scale: 1.25, color: "#00F0FF" }}
                  animate={{ scale: 1, color: "#F8FAFC" }}
                  className="font-display text-4xl font-extrabold"
                  data-testid="modular-counter"
                >
                  {total}
                  <span className="text-lg text-slate-500">/{max}</span>
                </motion.p>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{t.modular.counterLabel}</p>
              </div>
            </div>

            <div className="mt-8 grid grid-cols-3 gap-3">
              {t.modular.base.map((m) => (
                <div key={m} className="rounded-lg border border-em/40 bg-em/10 p-3 text-center text-xs font-semibold text-em">
                  {m}
                </div>
              ))}
              {t.modular.addons.map((m) => {
                const on = added.includes(m);
                return (
                  <motion.div
                    key={m}
                    layout
                    animate={on ? { opacity: 1, scale: 1 } : { opacity: 0.35, scale: 0.97 }}
                    className={`rounded-lg border p-3 text-center text-xs font-medium ${
                      on ? "border-cy/50 bg-cy/15 text-cy" : "border-dashed border-slate-700 text-slate-600"
                    }`}
                  >
                    {m}
                  </motion.div>
                );
              })}
            </div>

            <div className="mt-8 h-1.5 overflow-hidden rounded-full bg-qcard">
              <motion.div
                className="h-full rounded-full bg-gradient-to-r from-cy to-em"
                animate={{ width: `${(total / max) * 100}%` }}
                transition={{ type: "spring", stiffness: 120, damping: 20 }}
              />
            </div>

            <p className="mt-6 border-l-2 border-cy/40 pl-4 text-sm leading-relaxed text-slate-400">{t.modular.note}</p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default ModularGrowth;
